/**
 * Native-app install + deep-link helpers for the campsite pages.
 *
 * One place for the app ids, the store URLs with install attribution, and the
 * links that open a campsite inside the app. The iOS and Android apps parse
 * these exact shapes — change them together (see mobile-app DeepLink parsers).
 *
 * Why a custom scheme and not universal links / App Links: Shopify serves
 * campingaustralia.com.au/.well-known/* itself (an empty AASA and `[]`
 * assetlinks), so neither OS can verify the domain for us. Safari's Smart App
 * Banner passes `app-argument` without any domain verification, and Android
 * Chrome's intent:// URL opens the app by package name with a Play fallback.
 *
 * Plain module (no 'use client') — server metadata and client code both use it.
 */
export const IOS_APP_ID = '6761671553';
export const ANDROID_PACKAGE = 'com.campingaustralia.android';
// Same values as AppStoreBadges' exports. Not imported from there: that file
// is 'use client', so on the server its exports are client references, not
// strings.
const APP_STORE_URL = `https://apps.apple.com/au/app/camping-australia/id${IOS_APP_ID}`;
const PLAY_STORE_URL = `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE}`;
export const APP_URL_SCHEME = 'campingaustralia';

/** Content of `<meta name="apple-itunes-app">`; pageUrl becomes the app-argument. */
export function appleItunesAppContent(pageUrl?: string): string {
  return pageUrl ? `app-id=${IOS_APP_ID}, app-argument=${pageUrl}` : `app-id=${IOS_APP_ID}`;
}

/** Slug of a campsite page, or null when the path is not one. */
export function campsiteSlugFromPath(pathname: string): string | null {
  const m = /\/camp-sites\/([^/?#]+)\/?$/.exec(pathname || '');
  if (!m) return null;
  try {
    return decodeURIComponent(m[1]).toLowerCase();
  } catch {
    return m[1].toLowerCase();
  }
}

/** Best-effort display name from a slug, used until the page supplies the real one. */
export function nameFromSlug(slug: string): string {
  return slug
    .split('-')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/** campingaustralia://campsite/<slug> — the in-app address of a campsite. */
export function campsiteAppUrl(slug: string): string {
  return `${APP_URL_SCHEME}://campsite/${encodeURIComponent(slug)}`;
}

/**
 * Play listing with an install referrer, so installs from each placement are
 * attributable in the Play console (Acquisition → referrer). utm_content
 * carries the campsite so the app can open it on first launch.
 */
export function playStoreUrl(source: string, slug?: string | null): string {
  const ref = [`utm_source=${source}`, 'utm_medium=web'];
  if (slug) ref.push(`utm_content=${slug}`);
  return `${PLAY_STORE_URL}&referrer=${encodeURIComponent(ref.join('&'))}`;
}

/**
 * App Store link. Campaign attribution (`pt=<provider token>&ct=<source>`)
 * needs the provider token from App Store Connect → App Analytics → Campaign
 * Generator; add it here once known rather than guessing one.
 */
export function appStoreUrl(_source: string): string {
  return APP_STORE_URL;
}

/**
 * Android Chrome / Samsung Internet / Firefox: opens the installed app on
 * this campsite; when the app is missing, the browser follows the fallback
 * to the Play listing instead.
 */
export function androidOpenCampsiteUrl(slug: string, source: string): string {
  const fallback = encodeURIComponent(playStoreUrl(source, slug));
  return (
    `intent://campsite/${encodeURIComponent(slug)}` +
    `#Intent;scheme=${APP_URL_SCHEME};package=${ANDROID_PACKAGE};` +
    `S.browser_fallback_url=${fallback};end`
  );
}

/** campingaustralia://shared/<token> — opens a shared trip in the app, which
 *  copies it into the viewer's account after sign-in. */
export function sharedTripAppUrl(token: string): string {
  return `${APP_URL_SCHEME}://shared/${encodeURIComponent(token)}`;
}

/**
 * Android: open the shared trip in the installed app, else Play. The install
 * referrer carries `shared:<token>` so a fresh install opens the trip on
 * first launch (the app's deferred deep link).
 */
export function androidOpenSharedTripUrl(token: string, source: string): string {
  const fallback = encodeURIComponent(playStoreUrl(source, `shared:${token}`));
  return (
    `intent://shared/${encodeURIComponent(token)}` +
    `#Intent;scheme=${APP_URL_SCHEME};package=${ANDROID_PACKAGE};` +
    `S.browser_fallback_url=${fallback};end`
  );
}

/**
 * True for iOS Safari proper — the only browser that renders Apple's Smart
 * App Banner. Chrome/Firefox/Edge/etc. on iOS get our own banner instead.
 */
export function isIosSafari(ua: string): boolean {
  return IOS_RE.test(ua) && SAFARI_RE.test(ua) && !IOS_OTHER_BROWSER_RE.test(ua);
}

// Shared with the pre-paint gate (lib/appBannerGate.ts), which inlines their
// .source into a <head> script — keep them free of flags other than `i`.
export const IOS_RE = /iPhone|iPod|iPad/i;
export const SAFARI_RE = /Safari\//;
export const IOS_OTHER_BROWSER_RE =
  /CriOS|FxiOS|EdgiOS|OPiOS|GSA\/|DuckDuckGo|YaBrowser|Instagram|FBAN|FBAV|Line\//;

/** Banner wording for a campsite page; generic elsewhere. */
export function bannerCopy(name: string | null): { title: string; subtitle: string } {
  if (name) {
    return {
      // Reads as one sentence across the two lines; the name leads so the
      // part that truncates on a narrow phone is the generic bit.
      title: `Save ${name}`,
      subtitle: 'to a trip in the free app · offline maps',
    };
  }
  return {
    title: 'Camping Australia app',
    subtitle: 'Free · 5,000+ campsites · offline maps',
  };
}
